// Copyright 2022 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license, please see LICENSE.md in the
// project root for license information or contact permission@sei.cmu.edu for full terms.

import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Subject, Observable, combineLatest } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import {
  ComnAuthService,
  ComnSettingsService,
  Theme,
  ComnAuthQuery,
} from '@cmusei/crucible-common';
import { UserDataService } from 'src/app/data/user/user-data.service';
import { UserQuery } from 'src/app/data/user/user.query';
import { CurrentUserQuery } from 'src/app/data/user/user.query';
import { PermissionDataService } from 'src/app/data/permission/permission-data.service';
import { SystemPermission } from 'src/app/generated/cite.api';
import { TopbarView } from './../shared/top-bar/topbar.models';
import { EvaluationDataService } from 'src/app/data/evaluation/evaluation-data.service';
import { EvaluationQuery } from 'src/app/data/evaluation/evaluation.query';
import {
  Evaluation,
  ItemStatus,
  Move,
  ScoringModel,
  Submission,
  Team,
  UnreadArticles,
  User,
} from 'src/app/generated/cite.api';
import { MoveDataService } from 'src/app/data/move/move-data.service';
import { MoveQuery } from 'src/app/data/move/move.query';
import { ScoringModelDataService } from 'src/app/data/scoring-model/scoring-model-data.service';
import { ScoringModelQuery } from 'src/app/data/scoring-model/scoring-model.query';
import { SubmissionDataService } from 'src/app/data/submission/submission-data.service';
import { SubmissionQuery } from 'src/app/data/submission/submission.query';
import { TeamDataService } from 'src/app/data/team/team-data.service';
import { TeamQuery } from 'src/app/data/team/team.query';
import { TeamMembershipDataService } from 'src/app/data/team/team-membership-data.service';
import { TeamRoleDataService } from 'src/app/data/team/team-role-data.service';
import {
  ApplicationArea,
  SignalRService,
} from 'src/app/services/signalr.service';
import { GallerySignalRService } from 'src/app/services/gallery-signalr.service';
import { UnreadArticlesQuery } from 'src/app/data/unread-articles/unread-articles.query';
import { UIDataService } from 'src/app/data/ui/ui-data.service';
import { XApiService } from 'src/app/services/xapi/xapi.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { Sort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';

export enum Section {
  dashboard = 'dashboard',
  scoresheet = 'scoresheet',
  report = 'report',
  aggregate = 'aggregate'
}

@Component({
  selector: 'app-home-app',
  templateUrl: './home-app.component.html',
  styleUrls: ['./home-app.component.scss'],
  standalone: false,
})
export class HomeAppComponent implements OnDestroy, OnInit {
  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator) set paginator(paginator: MatPaginator) {
    if (paginator) {
      this.evaluationDataSource.paginator = paginator;
    }
  }
  apiMessage = 'The API web service is not responding.';
  topbarTextBase = 'Set AppTopBarText in Settings';
  topbarText = 'blank';
  section = Section;
  selectedSection = Section.dashboard;
  loggedInUser$ = this.currentUserQuery.select();
  loggedInUserId = '';
  isAuthorizedUser = false;
  isSidebarOpen = true;
  private unsubscribe$ = new Subject();
  hideTopbar = false;
  topbarImage = this.settingsService.settings.AppTopBarImage;
  TopbarView = TopbarView;
  theme$: Observable<Theme>;
  selectedEvaluationId = '';
  activeEvaluation$ =
    this.evaluationQuery.selectActive() as Observable<Evaluation>;
  activeScoringModel$ =
    this.scoringModelQuery.selectActive() as Observable<ScoringModel>;
  evaluationList: Evaluation[] = [];
  evaluationList$ = this.evaluationQuery.selectAll();
  submissionList$ = this.submissionQuery.selectAll();
  activeTeam$ = this.teamQuery.selectActive() as Team;
  displayedSubmission: Submission;
  evaluationsAreLoading$ = this.evaluationQuery.selectLoading();
  currentMoveNumber = -1;
  displayedMoveNumber = -1;
  requestedMoveNumber = -1;
  userCurrentSubmission: Submission;
  unreadArticles$ =
    this.unreadArticlesQuery.selectActive() as Observable<UnreadArticles>;
  evaluationForLoadedSubmissions: Evaluation;
  moveList$ = this.moveQuery.selectAll() as Observable<Move[]>;
  sortedMoveList: Move[] = [];
  teamList$ = this.teamQuery.selectAll();
  myTeamId = '';
  myTeamId$ = new BehaviorSubject<string>('');
  activeSubmission$ =
    this.submissionQuery.selectActive() as Observable<Submission>;
  public filterString: string;
  userList: User[] = [];
  Evaluation: Evaluation[] = [];
  evaluationDataSource = new MatTableDataSource<Evaluation>(new Array<Evaluation>());
  public displayedColumns: string[] = ['description', 'status', 'dateCreated'];
  private isSubmissionDataServiceLoading: boolean;
  waitingForCurrentMoveNumber = 0;
  noChanges$ = new BehaviorSubject<boolean>(false);
  username = '';
  permissions: SystemPermission[] = [];
  canViewAdministration = false;
  readonly SystemPermission = SystemPermission;

  constructor(
    @Inject(DOCUMENT) private _document: HTMLDocument,
    private authService: ComnAuthService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private userDataService: UserDataService,
    private userQuery: UserQuery,
    private currentUserQuery: CurrentUserQuery,
    private permissionDataService: PermissionDataService,
    private settingsService: ComnSettingsService,
    private authQuery: ComnAuthQuery,
    private evaluationDataService: EvaluationDataService,
    private evaluationQuery: EvaluationQuery,
    private moveDataService: MoveDataService,
    private scoringModelDataService: ScoringModelDataService,
    private scoringModelQuery: ScoringModelQuery,
    private submissionDataService: SubmissionDataService,
    private submissionQuery: SubmissionQuery,
    private teamDataService: TeamDataService,
    private teamQuery: TeamQuery,
    private teamMembershipDataService: TeamMembershipDataService,
    private teamRoleDataService: TeamRoleDataService,
    private signalRService: SignalRService,
    private gallerySignalRService: GallerySignalRService,
    private moveQuery: MoveQuery,
    private unreadArticlesQuery: UnreadArticlesQuery,
    private uiDataService: UIDataService,
    private xApiService: XApiService,
    titleService: Title
  ) {
    const appTitle = this.settingsService.settings.AppTitle || 'Set AppTitle in Settings';
    titleService.setTitle(appTitle);
    this.topbarTextBase =
      this.settingsService.settings.AppTopBarText || this.topbarTextBase;
    this.topbarText = this.topbarTextBase;
    this.theme$ = this.authQuery.userTheme$;
    this.hideTopbar = this.inIframe();
    this.submissionDataService.unload();
    this.scoringModelDataService.unload();
    this.teamDataService.unload();
    this.submissionQuery.selectLoading().pipe(takeUntil(this.unsubscribe$)).subscribe(isLoading => {
      this.isSubmissionDataServiceLoading = isLoading;
    });
    // Set the display settings from config file
  }

  ngOnInit() {
    // load system permissions
    this.permissionDataService
      .load()
      .subscribe(
        (x) => {
          this.permissions = this.permissionDataService.permissions;
          this.canViewAdministration = this.permissionDataService.canViewAdministration();
        }
      );
    // load evaluation permissions
    this.permissionDataService.loadEvaluationPermissions().subscribe();
    // load team permissions
    this.permissionDataService.loadTeamPermissions().subscribe();
    // observe the vital information and process it when it is all present
    combineLatest([
      this.evaluationList$,
      this.moveList$,
      this.loggedInUser$,
      this.teamList$,
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([evaluations, moves, user, teams]) => {
        // set the top bar text
        this.evaluationList = evaluations;
        this.sortedMoveList = moves.sort((a, b) =>
          +a.moveNumber < +b.moveNumber ? -1 : 1
        );
        const numberOfEvaluations = evaluations ? evaluations.length : 0;
        let evaluation: Evaluation;
        // if an evaluation has been selected, make it the active one
        if (
          this.selectedEvaluationId &&
          evaluations &&
          evaluations.length > 0
        ) {
          evaluation = evaluations.find(
            (e) => e.id === this.selectedEvaluationId
          );
          this.currentMoveNumber = evaluation.currentMoveNumber;
          this.evaluationDataService.setActive(evaluation.id);
        }
        if (evaluation) {
          this.selectedEvaluationId = evaluation.id;
          this.displayedMoveNumber = this.uiDataService.getMoveNumber(
            this.selectedEvaluationId
          );
          this.displayedMoveNumber =
            this.displayedMoveNumber >= 0 &&
              this.displayedMoveNumber <= evaluation.currentMoveNumber
              ? this.displayedMoveNumber
              : evaluation.currentMoveNumber;
          this.uiDataService.setMoveNumber(
            this.selectedEvaluationId,
            this.displayedMoveNumber
          );
          if (moves.length > 0 && !this.moveQuery.getActive()) {
            if (!this.moveQuery.getActive()) {
              const move = this.moveQuery
                .getAll()
                .find((m) => m.moveNumber === this.displayedMoveNumber);
              if (move) {
                this.moveDataService.setActive(move.id);
              }
            }
          }
          // if we intiated advancing to next evaluation move, then display it
          if (this.waitingForCurrentMoveNumber > 0) {
            const newMove = this.sortedMoveList.find(
              (m) => +m.moveNumber === +this.waitingForCurrentMoveNumber
            );
            if (newMove) {
              this.waitingForCurrentMoveNumber = 0;
              this.nextDisplayedMove(newMove);
            }
          }
        }
        // get the current user information
        if (user.name) {
          this.username = user.name;
          this.isAuthorizedUser = !!user.id;
          this.loggedInUserId = user.id;
        }
        // set the teams
        this.setTeams(teams);
      });
    this.userDataService.setCurrentUser();
    // observe active evaluation
    (this.evaluationQuery.selectActive() as Observable<Evaluation>).pipe(takeUntil(this.unsubscribe$)).subscribe(active => {
      if (active && active.id !== this.selectedEvaluationId) {
        this.selectedEvaluationId = active.id;
        this.loadEvaluationData();
        this.noChanges$.next(active.status === ItemStatus.Complete);
      }
    });
    //get users
    this.userQuery.selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((users) => {
        this.userList = users;
      });
    // observe route changes
    this.activatedRoute.queryParamMap.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      // get and set the evaluation
      const evaluationId = params.get('evaluation');
      if (evaluationId) {
        this.evaluationDataService.setActive(evaluationId);
        this.uiDataService.setEvaluation(evaluationId);
      } else {
        this.evaluationDataService.setActive('');
        this.selectedEvaluationId = '';
      }
      // get the requested section or set the saved section
      const section = params.get('section');
      switch (section) {
        case 'scoresheet':
          // set scoresheet
          this.selectedSection = Section.scoresheet;
          this.uiDataService.setSection(evaluationId, Section.scoresheet);
          // now remove the section from the url, so that refreshes work properly
          this.router.navigate([], {
            queryParams: { evaluation: evaluationId },
          });
          break;
        case 'dashboard':
          // set dashboard
          this.selectedSection = Section.dashboard;
          this.uiDataService.setSection(evaluationId, Section.dashboard);
          // now remove the section from the url, so that refreshes work properly
          this.router.navigate([], {
            queryParams: { evaluation: evaluationId },
          });
          break;
        default:
          // get the saved section
          const savedSection = this.uiDataService.getSection(evaluationId);
          if (savedSection === 'scoresheet') {
            this.selectedSection = Section.scoresheet;
          } else if (savedSection === 'report') {
            this.selectedSection = Section.report;
          } else if (savedSection === 'aggregate') {
            this.selectedSection = Section.aggregate;
          } else {
            this.selectedSection = Section.dashboard;
          }
          break;
      }
      // xAPI for team members (not observers)
      if (evaluationId && this.myTeamId) {
        const activeTeamId = this.teamQuery.getActiveId();
        if (activeTeamId === this.myTeamId) {
          // viewed
          if (this.selectedSection === Section.dashboard) {
            this.xApiService
              .viewedEvaluationDashboard(evaluationId)
              .pipe(take(1))
              .subscribe();
          } else if (this.selectedSection === Section.scoresheet) {
            this.xApiService
              .viewedEvaluationScoresheet(evaluationId)
              .pipe(take(1))
              .subscribe();
          }
        }
      }
    });
    // observe the submissions
    this.submissionList$.pipe(takeUntil(this.unsubscribe$)).subscribe(submissions => {
      this.processSubmissions(submissions);
    });
    // observe the evaluations
    this.evaluationQuery.selectAll().pipe(takeUntil(this.unsubscribe$)).subscribe(evaluations => {
      this.evaluationList = evaluations
        .sort((a, b) => {
          const aDescription = a.description.toLowerCase();
          const bDescription = b.description.toLowerCase();

          if (aDescription < bDescription) {
            return -1;
          } else if (aDescription > bDescription) {
            return 1;
          } else {
            return 0;
          }
        });
      this.setDataSources();
      // Load users for each evaluation (for displaying creator names)
      evaluations.forEach(evaluation => {
        if (evaluation.id) {
          this.userDataService.loadByEvaluation(evaluation.id).pipe(take(1)).subscribe();
        }
      });
    });
    // load the user's evaluations
    this.evaluationDataService.loadMine();
    // load team roles
    this.teamRoleDataService.loadRoles().subscribe();
    // join signalR
    this.signalRService
      .startConnection(ApplicationArea.home)
      .then(() => {
        this.signalRService.join();
      })
      .catch(() => {
        // the service reports the failure to the user
      });
    if (this.settingsService.settings.GalleryApiUrl) {
      this.gallerySignalRService
        .startConnection()
        .then(() => {
          this.gallerySignalRService.join();
        })
        .catch(() => {
          // the service reports the failure to the user
        });
    }
    const thisScope = this;
    this.filterString = '';
    this.evaluationDataSource.sort = this.sort;
  }

  setDataSources() {
    this.evaluationDataSource.data = this.evaluationList;
  }

  loadEvaluationData() {
    const evaluation = this.evaluationQuery
      .getAll()
      .find((e) => e.id === this.selectedEvaluationId);
    if (evaluation) {
      this.evaluationDataService.setActive(this.selectedEvaluationId);
      this.scoringModelDataService.loadById(evaluation.scoringModelId);
      this.moveDataService.loadByEvaluation(this.selectedEvaluationId);
      this.teamDataService.loadMine(this.selectedEvaluationId);
      this.userDataService.loadByEvaluation(this.selectedEvaluationId).pipe(take(1)).subscribe();
      this.currentMoveNumber = evaluation.currentMoveNumber;
    }
  }

  selectingAnEvaluation(evaluationId: string) {
    this.uiDataService.setSection(evaluationId, Section.dashboard);
  }

  nextDisplayedMove(move: Move) {
    this.moveDataService.setActive(move.id);
    this.uiDataService.setMoveNumber(
      this.selectedEvaluationId,
      move.moveNumber
    );
    this.displayedMoveNumber = move.moveNumber;
    const displayedSubmission = this.submissionQuery.getActive() as Submission;
    const submissions = this.submissionQuery.getAll();
    let newSubmission = submissions.find(
      (s) =>
        +s.moveNumber === +move.moveNumber &&
        s.userId === displayedSubmission.userId &&
        s.teamId === displayedSubmission.teamId &&
        s.groupId === displayedSubmission.groupId &&
        s.scoreIsAnAverage === displayedSubmission.scoreIsAnAverage
    );
    if (newSubmission) {
      this.setActiveSubmission(newSubmission);
    } else {
      // the new submission would not be allowed, so select the default submission
      if (this.myTeamId === (this.teamQuery.getActive() as Team).id) {
        // select the user score
        newSubmission = submissions.find(
          (s) =>
            +s.moveNumber === +move.moveNumber &&
            s.userId &&
            s.teamId &&
            !s.groupId &&
            !s.scoreIsAnAverage
        );
      } else {
        // select the team score
        newSubmission = submissions.find(
          (s) =>
            +s.moveNumber === +move.moveNumber &&
            !s.userId &&
            s.teamId &&
            !s.groupId &&
            !s.scoreIsAnAverage
        );
      }
      if (newSubmission) {
        this.setActiveSubmission(newSubmission);
      }
    }
  }

  previousDisplayedMove(move: Move) {
    this.moveDataService.setActive(move.id);
    this.uiDataService.setMoveNumber(
      this.selectedEvaluationId,
      move.moveNumber
    );
    this.displayedMoveNumber = move.moveNumber;
    const displayedSubmission = this.submissionQuery.getActive() as Submission;
    const newSubmission = this.submissionQuery
      .getAll()
      .find(
        (s) =>
          +s.moveNumber === +move.moveNumber &&
          s.userId === displayedSubmission.userId &&
          s.teamId === displayedSubmission.teamId &&
          s.groupId === displayedSubmission.groupId &&
          s.scoreIsAnAverage === displayedSubmission.scoreIsAnAverage
      );
    if (newSubmission) {
      this.setActiveSubmission(newSubmission);
    }
  }

  nextEvaluationMove(moveNumber: number) {
    const evaluation = this.evaluationList.find(
      (e) => e.id === this.selectedEvaluationId
    );
    if (+moveNumber <= +this.getMaxMoveNumber()) {
      const updateEvaluation = { ...evaluation };
      updateEvaluation.currentMoveNumber = moveNumber;
      this.evaluationDataService.changeCurrentMove(updateEvaluation);
      this.waitingForCurrentMoveNumber = moveNumber;
    }
  }

  getMaxMoveNumber() {
    return this.sortedMoveList.length > 0
      ? this.sortedMoveList[this.sortedMoveList.length - 1].moveNumber
      : +this.currentMoveNumber;
  }

  setTeams(teams: Team[]) {
    // check if saved teamId is in the list of teams
    const savedTeamId = this.uiDataService.getTeam(this.selectedEvaluationId);
    let activeTeamId = teams.some((t) => t.id === savedTeamId)
      ? savedTeamId
      : '';
    // find the user's team
    teams.forEach((t) => {
      if (t.memberships.some((m) => m.userId === this.loggedInUserId)) {
        this.myTeamId = t.id;
        this.myTeamId$.next(t.id);
        // if the saved team wasn't in the list, set the user's team to the active team
        if (!activeTeamId) {
          activeTeamId = t.id;
          this.uiDataService.setTeam(this.selectedEvaluationId, activeTeamId);
        }
      }
    });
    if (activeTeamId) {
      this.changeTeam(activeTeamId);
      this.teamMembershipDataService.loadMemberships(activeTeamId);
    }
  }

  changeTeam(teamId: string) {
    let oldTeamId = this.teamQuery.getActiveId();
    const teamChanged = oldTeamId !== teamId;
    if (teamChanged) {
      // make sure to send a Guid for old team ID
      oldTeamId = oldTeamId ? oldTeamId : teamId;
      // signalR hub: leave the old team and join the new team
      this.signalRService.switchTeam(oldTeamId, teamId);
    }
    // when observing a team, you can't see the user or the team average
    if (teamId && this.myTeamId && teamId !== this.myTeamId) {
      if (
        this.uiDataService.getSubmissionType(this.selectedEvaluationId) ===
        'user' ||
        this.uiDataService.getSubmissionType(this.selectedEvaluationId) ===
        'team-avg'
      ) {
        this.uiDataService.setSubmissionType(this.selectedEvaluationId, 'team');
      }
    }
    this.teamDataService.setActive(teamId);
    this.uiDataService.setTeam(this.selectedEvaluationId, teamId);
    this.submissionDataService.setActive('');
    this.moveDataService.setActive('');
    this.submissionDataService.unload();
    this.submissionDataService.loadByEvaluationTeam(
      this.selectedEvaluationId,
      teamId
    );
    // Only log xAPI observed statement when team actually changes
    if (teamChanged && this.myTeamId && teamId && this.selectedEvaluationId && teamId !== this.myTeamId) {
      if (this.selectedSection === Section.dashboard) {
        this.xApiService
          .observedEvaluationDashboard(this.selectedEvaluationId, teamId)
          .pipe(take(1))
          .subscribe();
      } else if (this.selectedSection === Section.scoresheet) {
        this.xApiService
          .observedEvaluationScoresheet(this.selectedEvaluationId, teamId)
          .pipe(take(1))
          .subscribe();
      }
    }
  }

  changeSection(section: string) {
    this.selectedSection = section as Section;
    this.uiDataService.setSection(this.selectedEvaluationId, section);

    // Log xAPI when changing sections
    const activeTeamId = this.teamQuery.getActiveId();
    if (this.selectedEvaluationId && activeTeamId && this.myTeamId) {
      if (activeTeamId === this.myTeamId) {
        // Viewing own team
        if (section === Section.dashboard) {
          this.xApiService
            .viewedEvaluationDashboard(this.selectedEvaluationId)
            .pipe(take(1))
            .subscribe();
        } else if (section === Section.scoresheet) {
          this.xApiService
            .viewedEvaluationScoresheet(this.selectedEvaluationId)
            .pipe(take(1))
            .subscribe();
        }
      } else {
        // Observing another team
        if (section === Section.dashboard) {
          this.xApiService
            .observedEvaluationDashboard(this.selectedEvaluationId, activeTeamId)
            .pipe(take(1))
            .subscribe();
        } else if (section === Section.scoresheet) {
          this.xApiService
            .observedEvaluationScoresheet(this.selectedEvaluationId, activeTeamId)
            .pipe(take(1))
            .subscribe();
        }
      }
    }
  }

  processSubmissions(submissions) {
    // process submissions
    const activeTeam = this.teamQuery.getActive() as Team;
    const scoringModel = this.scoringModelQuery.getActive() as ScoringModel;
    if (!activeTeam || !scoringModel) {
      return;
    }
    if (submissions.some(s => s.teamId && s.teamId === activeTeam.id)) {
      let activeSubmission = this.submissionQuery.getActive() as Submission;
      activeSubmission = activeSubmission
        ? submissions.find((s) => s.id === activeSubmission.id)
        : null;
      if (
        !activeSubmission ||
        activeSubmission.submissionCategories.length === 0
      ) {
        let submissionType = this.uiDataService.getSubmissionType(
          this.selectedEvaluationId
        );
        if (!submissionType) {
          submissionType = 'user';
        }
        if (submissionType === 'user' && !scoringModel.useUserScore) {
          submissionType = 'team';
        }
        this.selectDisplayedSubmission(submissionType);
      }
    }
  }

  setActiveSubmission(submission: Submission) {
    if (submission.id != this.submissionQuery.getActiveId()) {
      this.submissionDataService.setActive(submission.id);
      if (!submission.scoreIsAnAverage) {
        this.submissionDataService.loadById(submission.id);
      }
      const activeMove = this.sortedMoveList.find(
        (m) => +m.moveNumber === +submission.moveNumber
      );
      this.moveDataService.setActive(activeMove.id);
    }
  }

  selectDisplayedSubmission(selection: string) {
    if (selection === 'report') {
      this.selectedSection =
        this.selectedSection === this.section.report
          ? this.section.dashboard
          : this.section.report;
    } else {
      const submissions = this.submissionQuery.getAll();
      let newSubmission: Submission = null;
      switch (selection) {
        case 'user':
          newSubmission = submissions.find(
            (s) =>
              +s.moveNumber === +this.displayedMoveNumber &&
              s.userId === this.loggedInUserId
          );
          break;
        case 'team':
          newSubmission = submissions.find(
            (s) =>
              +s.moveNumber === +this.displayedMoveNumber &&
              s.userId === null &&
              s.teamId !== null &&
              !s.scoreIsAnAverage
          );
          break;
        case 'team-avg':
          newSubmission = submissions.find(
            (s) =>
              +s.moveNumber === +this.displayedMoveNumber &&
              s.userId === null &&
              s.teamId !== null &&
              s.scoreIsAnAverage
          );
          break;
        case 'group-avg':
          newSubmission = submissions.find(
            (s) =>
              +s.moveNumber === +this.displayedMoveNumber &&
              s.userId === null &&
              s.teamId === null &&
              s.scoreIsAnAverage
          );
          break;
        case 'official':
          newSubmission = submissions.find(
            (s) =>
              +s.moveNumber === +this.displayedMoveNumber &&
              s.userId === null &&
              s.teamId === null &&
              s.groupId === null
          );
          break;
        default:
          break;
      }
      if (newSubmission) {
        this.uiDataService.setSubmissionType(this.selectedEvaluationId, selection);
        this.setActiveSubmission(newSubmission);
      }
    }
  }

  logout() {
    this.authService.logout();
  }

  inIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  getAppContentClass() {
    if (this.inIframe()) {
      return 'app-model-container elevate app-score-container';
    } else {
      return 'app-model-container elevate app-score-container';
    }
  }

  getUsername(userId: string): string {
    const user = this.userList.find((u) => u.id === userId);
    return user ? user.name : ' ';
  }

  //Filter evaluation based on description value
  applyFilter(filterValue: string) {
    this.filterString = filterValue;
    filterValue = filterValue.trim();
    filterValue = filterValue.toLowerCase();
    this.evaluationDataSource.filter = filterValue;
  }

  //clear text on filter search bar
  clearFilter() {
    this.applyFilter('');
  }

  //sort data based on evaluation's description
  sortData(sort: Sort) {
    const data = this.evaluationList.slice();
    this.evaluationDataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      return this.compare(a.description, b.description, isAsc);
    });
  }

  //compare function for evaluation's descriptions
  compare(a: string, b: string, isAsc: boolean) {
    if (a === null || b === null) {
      return 0;
    } else {
      return (a.toLowerCase() < b.toLowerCase() ? -1 : 1) * (isAsc ? 1 : -1);
    }
  }

  topBarNavigate(url): void {
    this.router.navigate(['/']).then(() => {
      window.location.reload();
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
    this.signalRService.leave();
    this.gallerySignalRService.leave();
  }
}
